import {
  Group,
  Mesh,
  MeshBasicMaterial,
  Vector2,
  Vector3,
} from "three";
import { ConvexGeometry } from "three/examples/jsm/geometries/ConvexGeometry.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial.js";
import { LineSegments2 } from "three/examples/jsm/lines/LineSegments2.js";
import { LineSegmentsGeometry } from "three/examples/jsm/lines/LineSegmentsGeometry.js";

import type { FigureData } from "../data/octagonOnCube";

/**
 * 3D 部分の図形（凸包メッシュ + 太いライン）を構築する。
 * 点 (Dot) は CSS2DRenderer 上のオーバーレイで描画するため、ここには含めない。
 *
 * - Hull: ConvexGeometry を背景色で描画、polygonOffset で深度を奥にバイアス
 *         して裏面の点・辺を z テストで遮蔽する
 * - Lines: LineSegments2 + LineMaterial で pixel 単位の太さを指定可能
 *
 * raycaster でのオクルージョン判定に使うため、hull は public で公開。
 */
export class Figure {
  readonly group: Group;
  readonly hull: Mesh;

  private readonly lines: LineSegments2;
  private readonly linesMat: LineMaterial;

  constructor(data: FigureData) {
    const linePositions: number[] = [];
    for (const [a, b] of data.edges) {
      const va = data.vertices[a];
      const vb = data.vertices[b];
      linePositions.push(va[0], va[1], va[2], vb[0], vb[1], vb[2]);
    }
    const linesGeom = new LineSegmentsGeometry();
    linesGeom.setPositions(linePositions);

    this.linesMat = new LineMaterial({
      color: 0x1a1a1a,
      linewidth: 1.5,
      transparent: true,
      opacity: 0.95,
      resolution: new Vector2(1, 1),
      dashed: false,
    });
    this.lines = new LineSegments2(linesGeom, this.linesMat);
    this.lines.computeLineDistances();

    const hullVerts = data.vertices.map(
      ([x, y, z]) => new Vector3(x, y, z),
    );
    const hullGeom = new ConvexGeometry(hullVerts);
    const hullMat = new MeshBasicMaterial({
      color: 0xffffff,
      polygonOffset: true,
      polygonOffsetFactor: 1,
      polygonOffsetUnits: 4,
    });
    this.hull = new Mesh(hullGeom, hullMat);
    this.hull.renderOrder = -1;

    this.group = new Group();
    this.group.add(this.hull);
    this.group.add(this.lines);
  }

  setOcclusion(enabled: boolean): void {
    this.hull.visible = enabled;
  }

  setShowLines(visible: boolean): void {
    this.lines.visible = visible;
  }

  setLineWidth(width: number): void {
    this.linesMat.linewidth = width;
  }

  setLineColor(color: string): void {
    this.linesMat.color.set(color);
  }

  setLinesOpacity(value: number): void {
    this.linesMat.opacity = value;
  }

  setResolution(width: number, height: number): void {
    this.linesMat.resolution.set(width, height);
  }

  /**
   * 頂点・辺の位置を差し替える (八角形比率の切替用)。トポロジ (辺の接続) は
   * 同じで位置だけ変わる前提。線ジオメトリと凸包を作り直す。
   */
  setGeometry(data: FigureData): void {
    const linePositions: number[] = [];
    for (const [a, b] of data.edges) {
      const va = data.vertices[a];
      const vb = data.vertices[b];
      linePositions.push(va[0], va[1], va[2], vb[0], vb[1], vb[2]);
    }
    this.lines.geometry.dispose();
    const linesGeom = new LineSegmentsGeometry();
    linesGeom.setPositions(linePositions);
    this.lines.geometry = linesGeom;
    this.lines.computeLineDistances();

    this.hull.geometry.dispose();
    this.hull.geometry = new ConvexGeometry(
      data.vertices.map(([x, y, z]) => new Vector3(x, y, z)),
    );
  }

  /** GPU バッファ / マテリアルを解放。kaleidoscope ring など Figure を
   * 動的に作り直す利用箇所で memory leak を防ぐために呼ぶ。 */
  dispose(): void {
    this.lines.geometry.dispose();
    this.linesMat.dispose();
    this.hull.geometry.dispose();
    (this.hull.material as MeshBasicMaterial).dispose();
  }
}
